import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Clock, TrendingUp, MessageCircle, Ban, Shield } from 'lucide-react';

export const Features = () => {
  const features = [
    {
      icon: Zap,
      title: 'Intelligent Upselling',
      description: '"Want fries with that?" automatically. Increases order value by 35% without any effort.',
      highlight: true
    },
    {
      icon: Clock,
      title: '24/7 Service - Never Close',
      description: '2 AM shawarma order? No problem. AI takes orders around the clock—even when you\'re closed.',
      highlight: false
    },
    {
      icon: TrendingUp,
      title: 'Update Menu in 30 Seconds',
      description: 'Out of beef? Update menu instantly. No reprinting. No staff confusion.',
      highlight: false
    },
    {
      icon: MessageCircle,
      title: 'Serves Multiple Customers at Once',
      description: '10 people ordering simultaneously? Easy. AI never gets overwhelmed.',
      highlight: true
    },
    {
      icon: Ban,
      title: 'Zero Wrong Orders',
      description: 'AI confirms every detail before sending to kitchen. No more "I said no onions" complaints.',
      highlight: false
    },
    {
      icon: Shield,
      title: 'Optional Pre-Payment',
      description: 'Collect payment before cooking. No more "forgot my wallet" situations.',
      highlight: false
    }
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-gradient-to-b from-background to-secondary/5" data-testid="features-section">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-primary bg-primary/10 px-4 py-2 rounded-full inline-block mb-4">
            Essential Features for Delivery Success
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your Digital Employee That Never Quits
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to scale delivery orders without the staff headaches.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className={`feature-card bg-white p-8 rounded-2xl border transition-all group ${feature.highlight ? 'border-primary/30 shadow-lg' : 'border-border/50 shadow-sm hover:shadow-md'
                  }`}
                data-testid={`feature-${index}`}
              >
                {feature.highlight && (
                  <div className="absolute top-4 right-4">
                    <span className="text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
                      BEST
                    </span>
                  </div>
                )}

                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${feature.highlight ? 'bg-primary/10' : 'bg-accent/30'
                  } group-hover:scale-110 transition-transform`}>
                  <Icon className={feature.highlight ? 'text-primary' : 'text-secondary'} strokeWidth={1.5} size={28} />
                </div>

                <h3 className="text-xl font-bold mb-3 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>

                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-16 relative rounded-3xl overflow-hidden max-w-5xl mx-auto"
        >
          <img
            src="https://images.unsplash.com/photo-1711633648859-1eac3e5969b9"
            alt="Delicious burger and fast food"
            className="w-full h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/50 to-transparent flex items-end p-8 md:p-12">
            <div className="text-white">
              <h3 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Focus on Cooking. We Handle Orders.
              </h3>
              <p className="text-lg text-white/90 mb-6 max-w-2xl">
                Stop answering phones. Stop hiring order-takers. Start earning more with zero stress.
              </p>
              <a
                href="https://app.easyorder.delivery/informacoes"
                data-testid="cta-see-results"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2"
              >
                Try Free for 30 Days
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;